package devxertech.com.pages.contactSection

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.css.alignItems
import com.varabyte.kobweb.compose.css.cursor
import com.varabyte.kobweb.compose.css.userSelect
import com.varabyte.kobweb.compose.css.zIndex
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import devxertech.com.theme.toSitePalette
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.border
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexGrow
import org.jetbrains.compose.web.css.flexShrink
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.left
import org.jetbrains.compose.web.css.marginLeft
import org.jetbrains.compose.web.css.marginRight
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.maxHeight
import org.jetbrains.compose.web.css.opacity
import org.jetbrains.compose.web.css.outline
import org.jetbrains.compose.web.css.overflowY
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.paddingLeft
import org.jetbrains.compose.web.css.paddingRight
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text

@Composable
fun PhoneFieldWithFlagDropdown(
    width: CSSLengthOrPercentageNumericValue = 100.percent,
    countries: List<Country>,
    selectedCountry: Country?,
    onCountryChange: (Country) -> Unit,
    phone: String,
    onPhoneChange: (String) -> Unit,
    onError: (String?) -> Unit,
) {
    val sitePalette = ColorMode.current.toSitePalette()

    var expanded by remember { mutableStateOf(false) }
    var isFocused by remember { mutableStateOf(false) }
    var searchQuery by remember { mutableStateOf("") }

    val filteredCountries = remember(searchQuery, countries) {
        if (searchQuery.isBlank()) countries
        else countries.filter {
            it.name.contains(searchQuery, ignoreCase = true) ||
                    it.iso.contains(searchQuery, ignoreCase = true) ||
                    it.dialCode.contains(searchQuery)
        }
    }

    // ✅ OUTSIDE container → one field
    Div(
        attrs = {
            style {
                width(width)
                height(62.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)

                backgroundColor(sitePalette.contactLeft.rgb)
                borderRadius(8.px)

                border(
                    1.px,
                    LineStyle.Solid,
                    if (isFocused) sitePalette.primary.rgb else sitePalette.contactLeft.rgb
                )

                paddingLeft(10.px)
                paddingRight(10.px)

                position(Position.Relative) // ✅ dropdown absolute
            }
        }
    ) {

        // ✅ LEFT SIDE: Country dropdown (flag + code)
        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    cursor(Cursor.Pointer)
                    userSelect(UserSelect.None)
                    flexShrink(0)
                }
                onClick { expanded = !expanded }
            }
        ) {
            // Flag
            Img(
                src = selectedCountry?.flagUrl ?: "",
                attrs = {
                    attr("alt", "")
                    style {
                        width(22.px)
                        height(16.px)
                        borderRadius(3.px)
                        marginRight(3.px)
                    }
                }
            )

            // Dial code
            SpanText(
                text = selectedCountry?.dialCode ?: "+--",
                modifier = Modifier.fontSize(14.px).color(sitePalette.contactText.rgb)
            )

            // small arrow
            SpanText(
                text = "▼",
                modifier = Modifier
                    .fontSize(12.px)
                    .color(sitePalette.contactText.rgb)
                    .margin(left = 6.px)
                    .styleModifier {
                        property("display", "inline-flex")
                        property("white-space", "nowrap !important")
                    }
            )

        }

        // ✅ Divider inside one field
        Div(
            attrs = {
                style {
                    width(1.px)
                    height(20.px)
                    backgroundColor(sitePalette.contactText.rgb)
                    marginLeft(8.px)
                    marginRight(6.px)
                    opacity(1)
                    flexShrink(0)
                }
            }
        )

        // ✅ RIGHT SIDE: Phone input
        Input(InputType.Text) {
            value(phone)

            onFocusIn { isFocused = true }
            onFocusOut { isFocused = false }

            onInput {
                val digitsOnly = it.value.filter { ch -> ch.isDigit() }

                // dial code digits count (e.g. +92 => 2)
                val dialDigits = selectedCountry?.dialCode?.count { it.isDigit() } ?: 0

                // full international digits = dial code digits + national digits
                val fullLength = dialDigits + digitsOnly.length
                when {
                    // ✅ full international max: 15 digits
                    fullLength > 15 -> return@onInput

                    // ✅ national max: 14 digits
                    digitsOnly.length > 14 -> return@onInput

                    digitsOnly.length in 7..14 -> {
                        onError(null)
                        onPhoneChange(digitsOnly)
                    }
                    else -> {
                        onError("Contact number must be 7 to 14 digits")
                        onPhoneChange(digitsOnly)
                    }
                }
            }

            style {
                flexGrow(1)
                border(0.px)
                outline("none")               // ✅ remove blue ring
                property("box-shadow", "none")

                fontSize(16.px)
                backgroundColor(Color.transparent)
                color(sitePalette.contactText.rgb)
            }

            placeholder("Contact Number")
        }

        // ✅ Dropdown list
        if (expanded) {
            Div(
                attrs = {
                    style {
                        position(Position.Absolute)
                        top(68.px)
                        left(0.px)

                        width(280.px)
                        maxHeight(320.px)
                        backgroundColor(sitePalette.contactLeft.rgb)
                        borderRadius(10.px)
                        border(1.px, LineStyle.Solid, sitePalette.contactText.rgb)
                        zIndex(9999)
                        padding(8.px)
                    }
                }
            ) {

                // ✅ Search bar
                Input(InputType.Text) {
                    value(searchQuery)
                    onInput { searchQuery = it.value }

                    style {
                        width(100.percent)
                        height(38.px)
                        borderRadius(8.px)
                        border(1.px, LineStyle.Solid, sitePalette.contactText.rgb)
                        paddingLeft(10.px)
                        paddingRight(10.px)
                        fontSize(13.px)
                        backgroundColor(Color.transparent)
                        color(sitePalette.contactText.rgb)
                        outline("none")
                        property("box-shadow", "none")
                    }

                    placeholder("Search country...")
                }

                // ✅ Scroll list (separate div so search stays sticky)
                Div(
                    attrs = {
                        style {
                            maxHeight(260.px)
                            overflowY("auto")
                            marginTop(8.px)
                        }
                    }
                ) {

                    if (filteredCountries.isEmpty()) {
                        Div(attrs = {
                            style {
                                padding(10.px)
                                fontSize(13.px)
                                color(sitePalette.contactText.rgb)
                                opacity(0.6)
                            }
                        }) {
                            Text("No country found")
                        }
                    } else {
                        filteredCountries.forEach { c ->
                            Div(
                                attrs = {
                                    style {
                                        display(DisplayStyle.Flex)
                                        alignItems(AlignItems.Center)
                                        padding(8.px)
                                        cursor(Cursor.Pointer)
                                        borderRadius(8.px)
                                    }
                                    onClick {
                                        onCountryChange(c)
                                        expanded = false
                                        searchQuery = ""
                                    }
                                }
                            ) {
                                Img(
                                    src = c.flagUrl,
                                    attrs = {
                                        attr("alt", "")
                                        style {
                                            width(22.px)
                                            height(16.px)
                                            borderRadius(3.px)
                                            marginRight(10.px)
                                        }
                                    }
                                )

                                SpanText(
                                    text = c.name,
                                    modifier = Modifier.fontSize(13.px).color(sitePalette.contactText.rgb)
                                        .margin(right = 10.px)
                                )

                                SpanText(
                                    text = c.dialCode,
                                    modifier = Modifier.fontSize(13.px).color(sitePalette.contactText.rgb)
                                        .margin(left = 5.px)
                                )
                            }
                        }
                    }
                }
            }
        }

//        if (expanded) {
//            Div(
//                attrs = {
//                    style {
//                        position(Position.Absolute)
//                        top(68.px)
//                        left(0.px)
//
//                        width(280.px)
//                        maxHeight(280.px)
//                        overflowY("auto")
//
//                        backgroundColor(sitePalette.contactLeft.rgb)
//                        borderRadius(10.px)
//                        border(1.px, LineStyle.Solid, sitePalette.contactText.rgb)
//                        zIndex(9999)
//                        padding(8.px)
//                    }
//                }
//            ) {
//                countries.forEach { c ->
//                    Div(
//                        attrs = {
//                            style {
//                                display(DisplayStyle.Flex)
//                                alignItems(AlignItems.Center)
//                                padding(8.px)
//                                cursor(Cursor.Pointer)
//                                borderRadius(8.px)
//                            }
//                            onClick {
//                                onCountryChange(c)
//                                expanded = false
//                            }
//                        }
//                    ) {
//                        Img(
//                            src = c.flagUrl,
//                            attrs = {
//                                style {
//                                    width(22.px)
//                                    height(16.px)
//                                    borderRadius(3.px)
//                                    marginRight(10.px)
//                                }
//                            }
//                        )
//
//                        SpanText(
//                            text = c.name,
//                            modifier = Modifier.fontSize(13.px).color(sitePalette.contactText.rgb)
//                                .margin(right = 10.px)
//                        )
//
//                        SpanText(
//                            text = c.dialCode,
//                            modifier = Modifier.fontSize(13.px).color(sitePalette.contactText.rgb)
//                                .margin(left = 5.px)
//                        )
//                    }
//                }
//            }
//        }
    }
}

//@Composable
//fun PhoneInputWithCountryCode(
//    width: CSSLengthOrPercentageNumericValue = 100.percent,
//    selectedCode: String,
//    onCodeChange: (String) -> Unit,
//    phone: String,
//    onPhoneChange: (String) -> Unit,
//    onError: (String?) -> Unit,   // new
//) {
//    val sitePalette = ColorMode.current.toSitePalette()
//    var isFocused by remember { mutableStateOf(false) }
//    Div(
//        attrs = {
//            style {
//                width(width)
//                display(DisplayStyle.Flex)
//                alignItems(AlignItems.Center)
//                height(62.px)
//                border(0.px)
//                backgroundColor(sitePalette.contactLeft.rgb)
//                borderRadius(6.px)
//                paddingLeft(8.px)
//                border(
//                    1.px,
//                    LineStyle.Solid,
//                    if (isFocused)
//                        sitePalette.primary.rgb             // focus color
//                    else
//                        sitePalette.contactLeft.rgb  // normal
//                )
//            }
//        }
//    ) {
//
//        // 🔹 Country code
//        Select(
//            attrs = {
//                style {
//                    border(0.px)
//                    backgroundColor(Color.transparent)
//                    fontSize(13.px)
//                    cursor(Cursor.Pointer)
//                    fontWeight("400")
//                    color(sitePalette.contactText.rgb)
//                    outline("none")
//                    paddingLeft(4.px)
//                    paddingRight(6.px)
//                    width(100.px)
//                    minWidth(80.px)
//                    maxWidth(100.px)
//                }
//                onChange { it.value?.let(onCodeChange) }
//            }
//        ) {
//            countryDialCodes.forEach { code ->
//                Option(
//                    value = code,
//                    attrs = { if (code == selectedCode) selected() }
//                ) { Text(code) }
//            }
//        }
//
//        // 🔹 Divider
//        Div(
//            attrs = {
//                style {
//                    width(1.px)
//                    height(24.px)
//                    backgroundColor(sitePalette.contactText.rgb)
//                    marginLeft(2.px)
//                    marginRight(10.px)
//                }
//            }
//        )
//
//        // 🔹 Phone input (VALIDATION ANDAR)
//        Input(InputType.Text) {
//            value(phone)
//            onFocusIn { isFocused = true }
//            onFocusOut { isFocused = false }
//            onInput {
//                val digitsOnly = it.value.filter { ch -> ch.isDigit() }
//
//                when {
//                    digitsOnly.length > 14 -> {
//                        return@onInput
//                    }
//
//                    digitsOnly.length in 7..14 -> {
//                        onError(null)
//                        onPhoneChange(digitsOnly)
//                    }
//
//                    else -> {
//                        onError("Contact number must be 7 to 14 digits")
//                        onPhoneChange(digitsOnly)
//                    }
//                }
//            }
//
//            style {
//                width(90.percent)
//                border(0.px)
//                outline("none")
//                fontSize(16.px)
//                backgroundColor(sitePalette.contactLeft.rgb)
//                color(sitePalette.contactText.rgb)
//                overflow(overflow = Overflow.Hidden)
//            }
//
//            placeholder("Contact Number")
//        }
//    }
//}

